document.addEventListener('DOMContentLoaded', () => {
  if (customElements.get('collection-anchor-tabbar')) return;

  class CollectionAnchorTabbar extends HTMLElement {
    constructor() {
      super();
      this.tabbarHeight = 0;
      this.storedOffsetTop = 0;
      this.headerHeight = 0;
      this.isRunningAnimation = false;
      this.isManualHighlight = false;
      this.pendingActiveId = null;
      this.lastActiveId = null;
      this.buffer = 20;
      this.prefersReducedMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;
      this.onScroll = null;
      this.onResize = null;
      this.scrollEndTimer = null;
      this.placeholder = null;
      this.headerEl = null;
    }

    connectedCallback() {
      this.root = this.closest('.collection-anchor-tabbar');
      this.useClassname = this.dataset.useClassname !== 'false';
      this.enableSticky = this.dataset.enableSticky !== 'false';
      this.enableHeaderSticky = this.dataset.enableHeaderSticky !== 'false';
      this.items = Array.from(this.querySelectorAll('.catb__item[data-target]'));
      this.scrollContainer = this.querySelector('.catb__scroll');
      this.headerEl = document.querySelector(
        '#shopify-section-header, #shopify-section-header-classic'
      );

      if (!this.root) return;

      this.initSticky();
      this.updateHeight();
      this.bindEvents();

      requestAnimationFrame(() => {
        this.handleStickyScroll();
        this.menuScrollEvent();
      });
      window.setTimeout(() => {
        this.captureOffsetTop();
        this.handleStickyScroll();
        this.menuScrollEvent();
      }, 200);
    }

    disconnectedCallback() {
      if (this.onScroll) {
        window.removeEventListener('scroll', this.onScroll);
      }
      if (this.onResize) {
        window.removeEventListener('resize', this.onResize);
      }
      if (this.scrollEndTimer) {
        window.clearTimeout(this.scrollEndTimer);
      }
      this.setHeaderBehind(false);
      this.placeholder?.remove();
    }

    initSticky() {
      if (!this.enableSticky || this.placeholder) return;

      this.placeholder = document.createElement('div');
      this.placeholder.className = 'catb__placeholder';
      this.placeholder.setAttribute('aria-hidden', 'true');
      this.root.insertAdjacentElement('afterend', this.placeholder);
    }

    captureOffsetTop() {
      if (this.root.classList.contains('is-fixed')) return;
      this.storedOffsetTop =
        this.root.getBoundingClientRect().top + window.scrollY;
    }

    getHeaderHeight() {
      return this.headerEl?.offsetHeight || 0;
    }

    getTargetSelector(id) {
      return this.useClassname ? `.${id}` : `#${id}`;
    }

    isElementVisible(element) {
      if (!element) return false;
      const style = window.getComputedStyle(element);
      return style.display !== 'none' && style.visibility !== 'hidden';
    }

    updateHeight() {
      this.tabbarHeight = this.root?.offsetHeight || this.offsetHeight || 0;
      this.headerHeight = this.getHeaderHeight();
    }

    setHeaderBehind(isBehind) {
      if (!this.enableHeaderSticky || !this.headerEl) return;
      this.headerEl.classList.toggle('z-index-1', isBehind);
    }

    handleStickyScroll() {
      if (!this.enableSticky) return;

      this.updateHeight();

      if (!this.root.classList.contains('is-fixed')) {
        this.captureOffsetTop();
      }

      const scrollY = window.scrollY;
      const shouldFix = scrollY >= this.storedOffsetTop;

      if (shouldFix) {
        if (!this.root.classList.contains('is-fixed')) {
          this.root.classList.add('is-fixed');
          this.placeholder.style.height = `${this.tabbarHeight}px`;
        }

        if (this.enableHeaderSticky) {
          this.setHeaderBehind(true);
        }
      } else {
        this.root.classList.remove('is-fixed');
        this.placeholder.style.height = '0';
        this.setHeaderBehind(false);
        this.captureOffsetTop();
      }
    }

    throttle(fn, delay) {
      let startTime = 0;
      return () => {
        const now = Date.now();
        if (now - startTime >= delay) {
          startTime = now;
          fn();
        }
      };
    }

    bindEvents() {
      this.querySelectorAll('.catb__item[data-target]').forEach((item) => {
        item.addEventListener('click', () => this.onItemClick(item));
      });

      this.onScroll = this.throttle(() => {
        this.handleStickyScroll();
        this.menuScrollEvent();
      }, 50);
      this.onResize = () => {
        const wasFixed = this.root.classList.contains('is-fixed');
        if (wasFixed) {
          this.root.classList.remove('is-fixed');
          this.placeholder.style.height = '0';
        }
        this.updateHeight();
        this.captureOffsetTop();
        if (wasFixed) {
          this.handleStickyScroll();
        }
      };

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });
    }

    scrollItemIntoView(item) {
      if (!this.scrollContainer || !item) return;

      const container = this.scrollContainer;
      const wrap = item.closest('.catb__item-wrap') || item;
      const behavior = this.prefersReducedMotion ? 'auto' : 'smooth';
      const isMobile = window.matchMedia('(max-width: 850px)').matches;

      const containerRect = container.getBoundingClientRect();
      const wrapRect = wrap.getBoundingClientRect();
      const itemLeft = wrapRect.left - containerRect.left + container.scrollLeft;
      const itemWidth = wrapRect.width;
      const containerWidth = container.clientWidth;
      const maxScroll = Math.max(0, container.scrollWidth - containerWidth);

      let targetScroll;

      if (isMobile) {
        targetScroll = itemLeft - (containerWidth - itemWidth) / 2;
      } else if (wrapRect.left < containerRect.left) {
        targetScroll = container.scrollLeft + (wrapRect.left - containerRect.left);
      } else if (wrapRect.right > containerRect.right) {
        targetScroll =
          container.scrollLeft + (wrapRect.right - containerRect.right);
      } else {
        return;
      }

      targetScroll = Math.max(0, Math.min(targetScroll, maxScroll));

      container.scrollTo({ left: targetScroll, behavior });
    }

    setActive(id) {
      this.querySelectorAll('.catb__item[data-target]').forEach((item) => {
        const isActive = item.dataset.target === id;
        item.classList.toggle('is-active', isActive);
        item.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
      this.lastActiveId = id;
    }

    getSectionTop(element) {
      return element.getBoundingClientRect().top + window.scrollY;
    }

    onItemClick(item) {
      const id = item.dataset.target;
      if (!id) return;

      const target = document.querySelector(this.getTargetSelector(id));
      const fallbackUrl = item.dataset.fallbackUrl;

      if (!target) {
        if (fallbackUrl) {
          window.location.href = fallbackUrl;
        }
        return;
      }

      this.setActive(id);
      this.isManualHighlight = true;
      this.pendingActiveId = id;
      this.scrollItemIntoView(item);

      const targetOffset = this.getSectionTop(target) - this.tabbarHeight;
      const currentScroll = window.scrollY;

      if (Math.abs(currentScroll - targetOffset) <= 2) return;

      this.isRunningAnimation = true;
      window.scrollTo({
        top: targetOffset,
        behavior: this.prefersReducedMotion ? 'auto' : 'smooth',
      });

      if (this.scrollEndTimer) {
        window.clearTimeout(this.scrollEndTimer);
      }

      this.scrollEndTimer = window.setTimeout(() => {
        this.isRunningAnimation = false;
        this.scrollItemIntoView(item);
      }, this.prefersReducedMotion ? 0 : 500);
    }

    menuScrollEvent() {
      if (!this.items.length || this.isRunningAnimation) return;

      const scrollTop = window.scrollY;
      const scrollPosition = scrollTop + this.tabbarHeight + this.buffer;
      let currentIndex = null;
      let firstVisibleId = null;

      this.items.forEach((item) => {
        if (!this.isElementVisible(item)) return;

        const dataId = item.dataset.target;
        if (!dataId) return;

        if (!firstVisibleId) {
          firstVisibleId = dataId;
        }

        const target = document.querySelector(this.getTargetSelector(dataId));
        if (target && this.isElementVisible(target)) {
          const sectionTop = this.getSectionTop(target);
          if (scrollPosition >= sectionTop) {
            currentIndex = dataId;
          }
        }
      });

      if (!currentIndex) {
        currentIndex = firstVisibleId || this.items[0]?.dataset.target;
      }

      if (this.isManualHighlight) {
        if (this.pendingActiveId && currentIndex === this.pendingActiveId) {
          this.isManualHighlight = false;
          this.pendingActiveId = null;
        } else {
          return;
        }
      }

      if (currentIndex && currentIndex !== this.lastActiveId) {
        this.setActive(currentIndex);
        const activeItem = this.querySelector(
          `.catb__item[data-target="${currentIndex}"]`
        );
        this.scrollItemIntoView(activeItem);
      }
    }
  }

  customElements.define('collection-anchor-tabbar', CollectionAnchorTabbar);
});
